import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Keypair } from '@solana/web3.js';
import bs58 from 'bs58';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Generate a REAL Solana keypair
 * Returns a proper Base58 public key that can receive SOL/tokens
 */
const generateSolanaWallet = () => {
  // Generate a real Solana keypair
  const keypair = Keypair.generate();
  
  // Get the public key as Base58 string (the address users can send to)
  const publicKey = keypair.publicKey.toBase58();
  
  // Get the secret key as Base58 string (for backup/recovery)
  const secretKey = bs58.encode(keypair.secretKey);
  
  // Also create a short display ID for UI (first 4 + last 4 chars)
  const shortId = `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`;
  
  return {
    publicKey,      // Full Solana address (Base58, ~44 chars)
    secretKey,      // Full secret key (Base58 encoded)
    shortId,        // Short display version
    createdAt: Date.now()
  };
};

/**
 * Restore wallet from secret key
 */
const restoreWalletFromSecretKey = (secretKeyBase58) => {
  try {
    const secretKeyBytes = bs58.decode(secretKeyBase58);
    const keypair = Keypair.fromSecretKey(secretKeyBytes);
    const publicKey = keypair.publicKey.toBase58();
    const shortId = `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`;
    
    return {
      publicKey,
      secretKey: secretKeyBase58,
      shortId,
      createdAt: Date.now()
    };
  } catch (e) {
    console.error('Failed to restore wallet:', e);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [walletAddress, setWalletAddress] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localWallet, setLocalWallet] = useState(null);

  // Check for existing LOCAL wallet on mount
  useEffect(() => {
    const checkConnection = async () => {
      // Check for existing local wallet
      const storedWallet = localStorage.getItem('solana_wallet');
      let addressToUse = 'default_session';
      
      if (storedWallet) {
        try {
          const wallet = JSON.parse(storedWallet);
          
          // Validate it has a proper Solana public key (Base58, ~44 chars)
          if (wallet.publicKey && wallet.publicKey.length >= 32) {
            setLocalWallet(wallet);
            setWalletAddress(wallet.publicKey);
            setConnected(true);
            addressToUse = wallet.publicKey;
          } else {
            // Old format wallet - migrate to new format
            console.log('[Auth] Migrating old wallet format...');
            const newWallet = generateSolanaWallet();
            localStorage.setItem('solana_wallet', JSON.stringify(newWallet));
            localStorage.setItem('player_id', newWallet.publicKey);
            setLocalWallet(newWallet);
            setWalletAddress(newWallet.publicKey);
            setConnected(true);
            addressToUse = newWallet.publicKey;
          }
        } catch (err) {
          console.error('Failed to parse local wallet:', err);
        }
      }
      
      // Also check old format and migrate
      const oldWallet = localStorage.getItem('burner_wallet');
      if (oldWallet && !storedWallet) {
        console.log('[Auth] Found old burner_wallet, creating new Solana wallet...');
        const newWallet = generateSolanaWallet();
        localStorage.setItem('solana_wallet', JSON.stringify(newWallet));
        localStorage.setItem('player_id', newWallet.publicKey);
        localStorage.removeItem('burner_wallet'); // Remove old format
        setLocalWallet(newWallet);
        setWalletAddress(newWallet.publicKey);
        setConnected(true);
        addressToUse = newWallet.publicKey;
      }
      
      // Fetch user data for the session
      try {
        const response = await axios.get(`${API}/user/${addressToUse}`, {
          timeout: 5000
        });
        setUser(response.data);
      } catch (err) {
        console.error('Error fetching initial user:', err);
      }
      
      setLoading(false);
    };
    
    checkConnection();
  }, []);

  // Fetch or create user when wallet connects
  const fetchUser = useCallback(async (address) => {
    const sessionId = address || 'default_session';
    
    setError(null);
    
    try {
      const response = await axios.get(`${API}/user/${sessionId}`, {
        timeout: 5000
      });
      setUser(response.data);
    } catch (err) {
      console.error('Error fetching user:', err);
      if (err.code !== 'ECONNABORTED') {
        setError(err.message);
      }
    }
  }, []);

  // Create local wallet (THE ONLY OPTION) - Now generates REAL Solana keypair
  const createLocalWallet = useCallback(async () => {
    setConnecting(true);
    setError(null);
    
    try {
      const wallet = generateSolanaWallet();
      
      // Store in localStorage
      localStorage.setItem('solana_wallet', JSON.stringify(wallet));
      localStorage.setItem('player_id', wallet.publicKey);
      localStorage.setItem('wallet_created', 'true');
      
      // Remove old format if exists
      localStorage.removeItem('burner_wallet');
      
      setLocalWallet(wallet);
      setWalletAddress(wallet.publicKey);
      setConnected(true);
      
      await fetchUser(wallet.publicKey);
      
      return wallet;
    } catch (err) {
      console.error('Wallet creation error:', err);
      setError(err.message);
      return null;
    } finally {
      setConnecting(false);
    }
  }, [fetchUser]);

  // Legacy connect function - now creates local wallet
  const connect = useCallback(async () => {
    return createLocalWallet();
  }, [createLocalWallet]);

  // Disconnect wallet
  const disconnect = useCallback(async () => {
    // Clear local wallet
    localStorage.removeItem('solana_wallet');
    localStorage.removeItem('burner_wallet');
    localStorage.removeItem('player_id');
    localStorage.removeItem('wallet_created');
    
    setLocalWallet(null);
    setWalletAddress(null);
    setConnected(false);
    setUser(null);
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    const sessionId = walletAddress || 'default_session';
    await fetchUser(sessionId);
  }, [walletAddress, fetchUser]);

  // Get the full Solana address (for deposits)
  const getSolanaAddress = useCallback(() => {
    return localWallet?.publicKey || walletAddress || null;
  }, [localWallet, walletAddress]);

  // Get short display address
  const getShortAddress = useCallback(() => {
    const addr = localWallet?.publicKey || walletAddress;
    if (!addr) return null;
    return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
  }, [localWallet, walletAddress]);

  const value = {
    user,
    walletAddress,
    connected,
    connecting,
    loading,
    error,
    connect,
    createLocalWallet,
    disconnect,
    refreshUser,
    localWallet,
    isAuthenticated: connected && !!walletAddress,
    // NEW: Solana address helpers
    getSolanaAddress,
    getShortAddress,
    solanaAddress: localWallet?.publicKey || walletAddress,
    secretKey: localWallet?.secretKey,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
